import { ReactElement, useState } from 'react';
import { FileInput, Heading1, LoadingSpinner, Paragraph } from '@motili/ui';
import { styled } from 'styled-components';
import { Link } from 'react-router-dom';

import { setDocuments } from '../../../common/redux/ducks/contractorRegistrationForm';
import { useAppDispatch, useAppSelector } from '../../../common/redux/hooks';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import FileCard from './FileCard';
import FileUploadFormFields, { FormFieldValues } from './FileUploadFormFields';
import ButtonContainer from '../../../components/layout/ButtonContainer';
import StyledButton from '../../../components/styled/StyledButton';
import { DOCUMENT_UPLOAD_FIELDS } from '../../../common/constants';
import {
    ContractorDocuments,
    DocFile,
    Qualification,
    RequiredFormFields,
} from '../../../common/types';

export interface MultipleFilesEntry {
    file: File;
    formFieldValues: FormFieldValues;
}

interface MultipleFilesUploadProps {
    fileNameId: string;
    label: string;
    paragraphText: string;
    handleBackClick: () => void;
    handleMultipleFilesUpload: (
        fileNameId: string,
        files: MultipleFilesEntry[]
    ) => Promise<unknown>;
}

const getExistingFileName = (entry: DocFile | Qualification): string =>
    'name' in entry ? entry.name : entry.fileName;

export default function MultipleFilesUpload({
    fileNameId,
    label,
    paragraphText,
    handleBackClick,
    handleMultipleFilesUpload,
}: Readonly<MultipleFilesUploadProps>): ReactElement {
    const dispatch = useAppDispatch();
    const documents = useAppSelector(
        ({ contractorRegistration }) =>
            contractorRegistration.contractor.documents
    );
    const [files, setFiles] = useState<MultipleFilesEntry[]>([]);
    const [loading, setLoading] = useState(false);

    const { requiredFormFields } =
        DOCUMENT_UPLOAD_FIELDS[
            fileNameId as keyof typeof DOCUMENT_UPLOAD_FIELDS
        ];

    const existingDocEntries = (documents[
        fileNameId as keyof ContractorDocuments
    ] ?? []) as Array<DocFile | Qualification>;

    const onDropAccepted = (acceptedFiles: File[]) => {
        const newEntries = acceptedFiles.map(file => ({
            file,
            formFieldValues: {},
        }));
        setFiles(prev => [...prev, ...newEntries]);
    };

    const handleValuesChange = (index: number, values: FormFieldValues) => {
        setFiles(prev =>
            prev.map((entry, i) =>
                i === index ? { ...entry, formFieldValues: values } : entry
            )
        );
    };

    const handleTrashClick = (index: number, isExisting: boolean) => {
        if (isExisting) {
            dispatch(
                setDocuments({
                    ...documents,
                    [fileNameId]: existingDocEntries.filter(
                        (_, i) => i !== index
                    ),
                } as ContractorDocuments)
            );
        } else {
            setFiles(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleSubmitClick = async () => {
        setLoading(true);
        await handleMultipleFilesUpload(fileNameId, files);
        setLoading(false);
        handleBackClick();
    };

    const allFilesValid =
        files.length > 0 &&
        files.every(({ formFieldValues }) =>
            requiredFormFields.every(
                field => !!formFieldValues[field as keyof FormFieldValues]
            )
        );

    return (
        <CenteredContainer hideProgressStepper>
            <StyledLink to='/contractor/documents'>
                <i className='fa fa-arrow-left' aria-hidden='true' />
                <span>{`Back to Documents`}</span>
            </StyledLink>
            <Heading1>{label}</Heading1>
            <StyledParagraph>{paragraphText}</StyledParagraph>
            <FileInput
                accept={{
                    'image/jpeg': [],
                    'image/png': [],
                    'application/pdf': [],
                    '.doc': [],
                    '.docx': [],
                }}
                multiple
                onDrop={() => {}}
                onDropAccepted={onDropAccepted}
                onDropRejected={() => {}}
                additionalInfoText={
                    'Accepted file types are .png, .pdf, .jpg, .doc, and .docx.'
                }
            />
            {existingDocEntries.map((doc, index) => (
                <FileCard
                    key={getExistingFileName(doc)}
                    fileName={getExistingFileName(doc)}
                    handleTrashClick={() => handleTrashClick(index, true)}
                />
            ))}
            {files.map((entry, index) => (
                <div key={entry.file.name}>
                    <FileCard
                        fileName={entry.file.name}
                        handleTrashClick={() => handleTrashClick(index, false)}
                    />
                    <FileUploadFormFields
                        fileNameId={fileNameId}
                        fullWidthFields={[RequiredFormFields.tradeType]}
                        onChange={values => handleValuesChange(index, values)}
                    />
                </div>
            ))}
            {loading ? <LoadingSpinner fullscreen busy={loading} /> : null}
            <ButtonContainer>
                <StyledButton
                    variant={'primary'}
                    disabled={!allFilesValid || loading}
                    onClick={handleSubmitClick}
                    buttonStyle={{ minWidth: 140 }}
                >
                    Confirm & Save
                </StyledButton>
            </ButtonContainer>
        </CenteredContainer>
    );
}

const StyledParagraph = styled(Paragraph)`
    margin-bottom: 20px;
`;

const StyledLink = styled(Link)`
    color: ${({ theme }) => theme.color('primary')};
    text-decoration: none;

    .fa {
        font-size: 14px;
        padding-right: 10px;
    }

    &:hover {
        color: ${({ theme }) => theme.color('hover')};
    }

    &:hover span {
        text-decoration: underline;
    }
`;
